import { ref, computed } from 'vue';
import { executeForCRUD } from '@/services/frontBack';
import { setAgentId } from '@/config/api';

interface WidgetAgentData {
  Id_Usuario: number;
  Id_Agente: number;
  Clave_Agente: string;
  Login: string;
  Id_Persona?: number;
  Guid_Usuario?: string;
  /** Token de sesión (Usuarios_Get ByGuid); requerido para PDF de cotización. */
  Token?: string;
}

interface WidgetAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  agentData: WidgetAgentData | null;
}

const WIDGET_STORAGE_KEY = 'widget_agent_data';

/**
 * Función helper para autenticar el widget fuera del contexto de Vue
 * Útil para usar en entry.ts antes de montar la app
 */
export const authenticateWidget = async (guidUsuario: string): Promise<WidgetAgentData | null> => {
  const { executeForCRUD } = await import('@/services/frontBack');
  const { setAgentId } = await import('@/config/api');

  try {
    // Obtener datos del usuario usando el GUID
    const userResponse = await executeForCRUD({
      ClassName: 'Usuarios_Get',
      Action: 'ByGuid',
      Guid_Usuario: guidUsuario,
    });

    if (!userResponse?.success || !userResponse?.data?.length) {
      console.error('Error al obtener datos del agente:', userResponse?.errorMessage);
      return null;
    }

    const userData = userResponse.data[0] as Record<string, unknown>;

    // Convertir Id_Persona a número si está disponible
    let idPersona: number | undefined = undefined;
    if (userData.Id_Persona !== undefined && userData.Id_Persona !== null) {
      const valorPersona = userData.Id_Persona;
      if (typeof valorPersona === 'number') {
        idPersona = valorPersona;
      } else if (typeof valorPersona === 'string' && !isNaN(Number(valorPersona))) {
        idPersona = Number(valorPersona);
      }
    }
    
    const token =
      typeof userData.Token === 'string' && userData.Token.trim()
        ? userData.Token.trim()
        : undefined;

    const agentData: WidgetAgentData = {
      Id_Usuario: Number(userData.Id_Usuario || 0),
      Id_Agente: Number(userData.Id_Agente || 0),
      Clave_Agente: String(userData.Clave_Agente || ''),
      Login: String(userData.Login || ''),
      Id_Persona: idPersona,
      Guid_Usuario: guidUsuario,
      Token: token,
    };

    // Validar que tenemos los datos necesarios
    if (!agentData.Id_Usuario || !agentData.Id_Agente || !agentData.Clave_Agente) {
      console.error('Datos incompletos del agente');
      return null;
    }

    // Guardar en localStorage
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(agentData));
      
      // También guardar en el formato que espera el sistema
      const userDataToStore = {
        Id_Usuario: agentData.Id_Usuario,
        Id: agentData.Id_Usuario,
        Id_Agente: agentData.Id_Agente,
        Clave_Agente: agentData.Clave_Agente,
        Login: agentData.Login,
        Id_Persona: agentData.Id_Persona,
        Guid: guidUsuario,
        ...(token ? { Token: token } : {}),
      };
      
      window.localStorage.setItem('user', JSON.stringify(userDataToStore));

      // Configurar el agentId en la configuración de la API
      setAgentId(String(agentData.Id_Agente));
    }

    return agentData;
  } catch (error) {
    console.error('Error al autenticar widget:', error);
    return null;
  }
};

export const useWidgetAuth = () => {
  const state = ref<WidgetAuthState>({
    isAuthenticated: false,
    isLoading: false,
    error: null,
    agentData: null,
  });

  /**
   * Obtiene los datos del agente usando el GUID del usuario
   * @param guidUsuario GUID del usuario/agente
   * @returns Datos del agente o null si hay error
   */
  const loginByGuid = async (guidUsuario: string): Promise<WidgetAgentData | null> => {
    state.value.isLoading = true;
    state.value.error = null;

    try {
      // Primero obtener datos del usuario usando el GUID
      const userResponse = await executeForCRUD({
        ClassName: 'Usuarios_Get',
        Action: 'ByGuid',
        Guid_Usuario: guidUsuario,
      });

      if (!userResponse?.success || !userResponse?.data?.length) {
        const errorMsg = userResponse?.errorMessage ?? 'No se pudo obtener la información del agente';
        state.value.error = errorMsg;
        state.value.isLoading = false;
        return null;
      }

      const userData = userResponse.data[0] as Record<string, unknown>;
      
      // Convertir Id_Persona a número si está disponible
      let idPersona: number | undefined = undefined;
      if (userData.Id_Persona !== undefined && userData.Id_Persona !== null) {
        const valorPersona = userData.Id_Persona;
        if (typeof valorPersona === 'number') {
          idPersona = valorPersona;
        } else if (typeof valorPersona === 'string' && !isNaN(Number(valorPersona))) {
          idPersona = Number(valorPersona);
        }
      }

      const token =
        typeof userData.Token === 'string' && userData.Token.trim()
          ? userData.Token.trim()
          : undefined;

      const agentData: WidgetAgentData = {
        Id_Usuario: Number(userData.Id_Usuario || 0),
        Id_Agente: Number(userData.Id_Agente || 0),
        Clave_Agente: String(userData.Clave_Agente || ''),
        Login: String(userData.Login || ''),
        Id_Persona: idPersona,
        Guid_Usuario: guidUsuario,
        Token: token,
      };

      // Validar que tenemos los datos necesarios
      if (!agentData.Id_Usuario || !agentData.Id_Agente || !agentData.Clave_Agente) {
        state.value.error = 'Datos incompletos del agente';
        state.value.isLoading = false;
        return null;
      }

      // Guardar en localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(agentData));
        
        // También guardar en el formato que espera el sistema
        window.localStorage.setItem('user', JSON.stringify({
          Id_Usuario: agentData.Id_Usuario,
          Id: agentData.Id_Usuario,
          Id_Agente: agentData.Id_Agente,
          Clave_Agente: agentData.Clave_Agente,
          Login: agentData.Login,
          Id_Persona: agentData.Id_Persona,
          Guid: guidUsuario,
          ...(token ? { Token: token } : {}),
        }));

        // Configurar el agentId en la configuración de la API
        setAgentId(String(agentData.Id_Agente));
      }

      state.value.agentData = agentData;
      state.value.isAuthenticated = true;
      state.value.isLoading = false;

      return agentData;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al autenticar';
      state.value.error = errorMsg;
      state.value.isLoading = false;
      return null;
    }
  };

  /**
   * Carga los datos del agente desde localStorage si existen
   */
  const loadFromStorage = (): WidgetAgentData | null => {
    if (typeof window === 'undefined') return null;

    try {
      const stored = window.localStorage.getItem(WIDGET_STORAGE_KEY);
      if (!stored) return null;

      const agentData = JSON.parse(stored) as WidgetAgentData;
      
      // Validar que los datos sean válidos
      if (!agentData.Id_Usuario || !agentData.Id_Agente || !agentData.Clave_Agente) {
        return null;
      }

      state.value.agentData = agentData;
      state.value.isAuthenticated = true;

      // Configurar el agentId
      setAgentId(String(agentData.Id_Agente));

      return agentData;
    } catch {
      return null;
    }
  };

  /**
   * Limpia la autenticación y los datos guardados
   */
  const logout = (): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(WIDGET_STORAGE_KEY);
      window.localStorage.removeItem('user');
      setAgentId(null);
    }

    state.value.isAuthenticated = false;
    state.value.agentData = null;
    state.value.error = null;
  };

  /**
   * Inicializa la autenticación del widget
   * Intenta cargar desde localStorage primero, si no existe, requiere hacer login
   * @param guidUsuario GUID del usuario (opcional si ya está guardado)
   */
  const initialize = async (guidUsuario?: string): Promise<boolean> => {
    // Primero intentar cargar desde localStorage
    const storedData = loadFromStorage();
    if (storedData) {
      return true;
    }

    // Si no hay datos guardados y se proporciona un GUID, hacer login
    if (guidUsuario) {
      const agentData = await loginByGuid(guidUsuario);
      return agentData !== null;
    }

    return false;
  };

  return {
    isAuthenticated: computed(() => state.value.isAuthenticated),
    isLoading: computed(() => state.value.isLoading),
    error: computed(() => state.value.error),
    agentData: computed(() => state.value.agentData),
    loginByGuid,
    loadFromStorage,
    logout,
    initialize,
  };
};

